
import { useToast } from "@/components/ui/use-toast";
import type { Word } from "@/lib/game-utils";

export function useGameToast() {
  const { toast } = useToast();

  const showSuccessToast = (message: string) => {
    toast({
      title: "Success",
      description: message,
      duration: 3000,
    });
  };

  const showWrongAnswerToast = (message?: string) => {
    if (toast) {
      toast({
        title: "Not quite right!",
        description: message || "Try again with a different combination.",
        duration: 3000,
      });
    }
  };

  // Enhanced function to format reasoning text with bold puzzle words, handling plural forms
  // and also preserving any existing bold tags in the text
  const formatReasoningWithBoldWords = (reasoning: string, puzzleWords: Word[] = []) => {
    console.log("Formatting reasoning:", reasoning);
    console.log("With puzzle words:", puzzleWords);
    
    // If reasoning is empty or no puzzle words provided, return as is
    if (!reasoning || !puzzleWords.length) {
      console.log("No reasoning or puzzle words provided");
      return reasoning || "No additional reasoning provided for this logic.";
    }
    
    // First, temporarily replace any existing bold tags to preserve them
    let formattedText = reasoning.replace(/<strong>/g, '§§BOLD_START§§')
                                .replace(/<\/strong>/g, '§§BOLD_END§§');
    
    // Process each puzzle word
    puzzleWords.forEach(word => {
      if (!word) return;
      
      // Create regex that matches the word plus common variations (plural forms)
      const escapedWord = String(word).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      
      // Match the exact word with word boundaries, and common plural forms
      const wordRegex = new RegExp(`\\b${escapedWord}(s|es|ies)?\\b`, 'gi');
      
      // Replace with bold tags
      formattedText = formattedText.replace(wordRegex, '<strong>$&</strong>');
    });
    
    // Restore the original bold tags
    formattedText = formattedText.replace(/§§BOLD_START§§/g, '<strong>')
                               .replace(/§§BOLD_END§§/g, '</strong>');
    
    console.log("Formatted text result:", formattedText);
    return formattedText;
  };

  const showReasoningToast = (reasoning: string, puzzleWords: Word[] = []) => {
    console.log("Showing reasoning toast with:", reasoning);
    console.log("Puzzle words for highlighting:", puzzleWords);
    
    // Set default message if reasoning is empty
    if (!reasoning || reasoning.trim() === "") {
      reasoning = "No additional reasoning provided for this logic.";
    }
    
    if (toast) {
      const formattedReasoning = formatReasoningWithBoldWords(reasoning, puzzleWords);
      console.log("Final formatted reasoning:", formattedReasoning);
      
      toast({
        title: "Logic Reasoning",
        description: (
          <div 
            className="prose dark:prose-invert max-w-none tracking-tight"
            dangerouslySetInnerHTML={{ 
              __html: formattedReasoning
            }} 
          />
        ),
        duration: Infinity, // Make it stay until manually dismissed
      });
    }
  };

  return {
    showWrongAnswerToast,
    showReasoningToast,
    showSuccessToast
  };
}
